// @ts-nocheck
import { createContext, useContext, useState, ReactNode } from "react";
import { translations, Language, TranslationKey } from "./translations";
import { ReadingCategory, CardReading } from "../types/i18n-types";
import { DEFAULT_LANGUAGE } from '../constants/languages';
import { CardName } from '../constants/tarot';
import { localStorageService } from '../services/localStorage';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: TranslationKey, params?: Record<string, string>) => string;
  tCard: (cardName: CardName) => string;
  tReading: (category: ReadingCategory, cardName: CardName) => CardReading | null;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>(() => {
    // Check localStorage first, then browser language, fallback to default
    const saved = localStorageService.getLanguage();
    if (saved && (saved === "en" || saved === "vi")) {
      return saved;
    }

    if (typeof window !== "undefined") {
      const browserLang = navigator.language.toLowerCase();
      if (browserLang.startsWith("vi")) {
        return "vi";
      }
    }
    return DEFAULT_LANGUAGE;
  });

  const handleSetLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorageService.setLanguage(lang);
  };

  const t = (key: TranslationKey, params?: Record<string, string>): string => {
    let translation = translations[language][key] as string;

    if (!translation) {
      // Fallback to English if translation not found
      translation = translations["en"][key] as string;
    }

    if (!translation) {
      return key; // Return key if no translation found
    }

    // Replace parameters if provided
    if (params) {
      Object.entries(params).forEach(([param, value]) => {
        translation = translation.replace(`{${param}}`, value);
      });
    }

    return translation;
  };

  const tCard = (cardName: CardName): string => {
    console.log(`[tCard] Looking up card: ${cardName}, language: ${language}`);
    const cardTranslations = translations[language].cards;
    // console.log(`[tCard] Available cards for ${language}:`, Object.keys(cardTranslations || {}));
    console.log(`[tCard] Direct lookup result:`, cardTranslations?.[cardName]);
    
    const result = (
      cardTranslations[cardName] ||
      translations["en"].cards[cardName] ||
      cardName
    );
    
    console.log(`[tCard] Final result for ${cardName}:`, result);
    return result;
  };

  const tReading = (category: ReadingCategory, cardName: CardName): CardReading | null => {
    console.log(`[tReading] Looking up reading: ${cardName} in category: ${category}, language: ${language}`);
    console.log(`[tReading] Full translations structure for ${language}:`, translations[language]);
    console.log(`[tReading] Readings property:`, translations[language].readings);
    
    const readingsData = translations[language].readings?.[category];
    console.log(`[tReading] Readings data for ${language}.${category}:`, readingsData ? 'exists' : 'missing');
    if (!readingsData) return null;

    // Check major arcana first
    const majorReading = readingsData.major?.[cardName];
    console.log(`[tReading] Major arcana lookup for ${cardName}:`, majorReading ? 'found' : 'not found');
    if (majorReading) return majorReading;

    // Check minor arcana in specific suits
    const suits = ['Cups', 'Swords', 'Wands', 'Pentacles'] as const;
    for (const suit of suits) {
      const suitData = readingsData.minor?.[suit];
      console.log('>>>> _ >>>> ~ LanguageContext.tsx:102 ~ suitData:', suitData)
      if (suitData) {
        const minorReading = suitData[cardName];
        console.log(`[tReading] Minor arcana lookup for ${cardName} in ${suit}:`, minorReading ? 'found' : 'not found');
        if (minorReading) return minorReading;
      }
    }

    // Fallback to English if not found
    if (language !== "en") {
      console.log(`[tReading] Falling back to English for ${cardName}`);
      const englishReadings = translations["en"].readings;
      if (englishReadings && englishReadings[category]) {
        const englishData = englishReadings[category];
        
        // Check English major arcana
        const englishMajor = englishData.major?.[cardName];
        console.log(`[tReading] English major arcana lookup for ${cardName}:`, englishMajor ? 'found' : 'not found');
        if (englishMajor) return englishMajor;

        // Check English minor arcana
        for (const suit of suits) {
          const suitData = englishData.minor?.[suit];
          if (suitData) {
            const englishMinor = suitData[cardName];
            console.log(`[tReading] English minor arcana lookup for ${cardName} in ${suit}:`, englishMinor ? 'found' : 'not found');
            if (englishMinor) return englishMinor;
          }
        }
      }
    }

    console.log(`[tReading] No reading found for ${cardName} in category ${category}`);
    return null;
  };

  const value = {
    language,
    setLanguage: handleSetLanguage,
    t,
    tCard,
    tReading,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
